/**
 * Partners landing page forms.
 *
 * Drives both partners-kit-form and partners-enquiry-form:
 *   - branches the enquiry form on the first question (demand vs transport supplier)
 *   - submits over admin-ajax and swaps in the pre-rendered success panel
 *   - re-fires the `partner_request` dataLayer event that WPForms 17687 fires today,
 *     so existing GA reporting keeps working after the switch to this block
 *   - pre-selects that first question for CTAs elsewhere on the page, so a visitor who
 *     already told us what they want is not asked again
 *
 * Success and error copy is rendered server-side and only revealed here, so Weglot
 * translates it (messages returned from PHP would bypass Weglot).
 */
(function () {
    'use strict';

    var config = window.a2aPartnersForms || {};
    var ajaxUrl = config.ajaxUrl || window.ajaxurl || '/wp-admin/admin-ajax.php';

    var ACTIONS = {
        kit: 'partners_kit_request',
        enquiry: 'partners_enquiry_submit'
    };

    // The enquiry block renders both of its branches inside one form. Each branch has
    // its own endpoint so demand and supply leads reach different n8n workflows.
    var BRANCH_ACTIONS = {
        supplier: 'partners_supplier_submit'
    };

    var DATALAYER_EVENTS = {
        kit: 'partner_kit_request',
        enquiry: 'partner_request',
        supplier: 'partner_supplier_request'
    };

    var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Breathing room between the sticky header and the section we scroll to.
    var SCROLL_GAP = 24;

    function show(el) {
        if (el) {
            el.hidden = false;
        }
    }

    function hide(el) {
        if (el) {
            el.hidden = true;
        }
    }

    /**
     * The container for a named branch. Matched by iteration rather than by attribute
     * selector because branch names come from an editor-managed ACF field.
     */
    function branchEl(form, name) {
        var found = null;

        form.querySelectorAll('[data-partners-branch]').forEach(function (el) {
            if (!found && el.getAttribute('data-partners-branch') === name) {
                found = el;
            }
        });

        return found;
    }

    /**
     * Reveal the branch matching the selected intent and hide the others.
     * Returns the active branch name, or '' when nothing is selected yet.
     */
    function applyBranch(form) {
        var select = form.querySelector('[data-partners-intent]');
        var branches = form.querySelectorAll('[data-partners-branch]');

        if (!select || !branches.length) {
            return '';
        }

        var option = select.options[select.selectedIndex];
        var active = (option && option.value) ? (option.getAttribute('data-branch') || 'form') : '';

        branches.forEach(function (branch) {
            var on = branch.getAttribute('data-partners-branch') === active;

            // FormData skips a disabled fieldset, so the hidden branch cannot contribute
            // values even though both branches use the same field names.
            branch.disabled = !on;

            if (on) {
                show(branch);
            } else {
                hide(branch);
            }
        });

        // Submit button, honeypot and error line are shared, so they follow the branches.
        if (active) {
            show(form.querySelector('[data-partners-footer]'));
        } else {
            hide(form.querySelector('[data-partners-footer]'));
        }

        return active;
    }

    function setBusy(form, busy) {
        var button = form.querySelector('button[type="submit"]');

        if (!button) {
            return;
        }

        button.disabled = busy;
        button.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    function submitForm(form, type, branch) {
        var errorEl = form.querySelector('[data-partners-error]');
        var section = form.closest('.partners-section');
        var successEl = section ? section.querySelector('[data-partners-success]') : null;
        var action = BRANCH_ACTIONS[branch] || ACTIONS[type];

        hide(errorEl);

        var data = new FormData(form);
        data.append('action', action);

        setBusy(form, true);

        fetch(ajaxUrl, {
            method: 'POST',
            credentials: 'same-origin',
            body: data
        })
            .then(function (response) {
                return response.json();
            })
            .then(function (payload) {
                if (payload && payload.success) {
                    hide(form);
                    show(successEl);

                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ event: DATALAYER_EVENTS[branch] || DATALAYER_EVENTS[type] });

                    return;
                }

                // Prefer the server's reason; the rendered fallback covers the rest.
                if (errorEl && payload && payload.data && payload.data.message) {
                    errorEl.textContent = payload.data.message;
                }

                show(errorEl);
                setBusy(form, false);
            })
            .catch(function () {
                show(errorEl);
                setBusy(form, false);
            });
    }

    function initForm(form) {
        var type = form.getAttribute('data-partners-form');

        if (!ACTIONS[type]) {
            return;
        }

        var select = form.querySelector('[data-partners-intent]');

        if (select) {
            applyBranch(form);
            select.addEventListener('change', function () {
                applyBranch(form);
            });
        }

        form.addEventListener('submit', function (event) {
            event.preventDefault();

            var branch = select ? applyBranch(form) : '';

            // No intent picked yet, so there is no branch to send.
            if (select && !branch) {
                select.focus();
                return;
            }

            var scope = branch ? branchEl(form, branch) : form;
            var emailField = (scope || form).querySelector('input[type="email"]');
            var email = emailField ? emailField.value.trim() : '';

            if (!EMAIL_RE.test(email)) {
                if (emailField) {
                    emailField.focus();
                }
                show(form.querySelector('[data-partners-error]'));
                return;
            }

            submitForm(form, type, branch);
        });
    }

    /**
     * Split an anchor target such as `#partners-contact?intent=supplier` into the element
     * id and the values to pre-fill. The query part is our own convention, not a real URL
     * query string, so it is parsed off the hash.
     *
     * `intent` picks the branch; any other parameter is matched against a field name in
     * that branch, which is how the API CTA arrives with a partner type and message
     * already filled in.
     */
    function parseTarget(hash) {
        var raw = String(hash || '').replace(/^#/, '');

        if (!raw) {
            return null;
        }

        var parts = raw.split('?');

        if (!parts[0]) {
            return null;
        }

        var params = new URLSearchParams(parts[1] || '');

        return {
            id: parts[0],
            intent: params.get('intent') || '',
            values: params
        };
    }

    function hasOption(select, value) {
        var i;

        for (i = 0; i < select.options.length; i++) {
            if (select.options[i].value === value) {
                return true;
            }
        }

        return false;
    }

    function fieldByName(scope, name) {
        var found = null;

        scope.querySelectorAll('input, select, textarea').forEach(function (field) {
            if (!found && field.getAttribute('name') === name) {
                found = field;
            }
        });

        return found;
    }

    /**
     * Fill fields in the open branch from the link's parameters, leaving everything
     * editable. Scoped to the branch on purpose: the honeypot sits in the shared footer,
     * so a crafted link cannot reach it and get a visitor's enquiry silently dropped.
     */
    function prefillBranch(branch, values) {
        if (!branch || !values) {
            return;
        }

        values.forEach(function (value, name) {
            if (name === 'intent' || value === '') {
                return;
            }

            var field = fieldByName(branch, name);

            // Never overwrite what the visitor has already typed.
            if (!field || field.value !== '') {
                return;
            }

            if (field.tagName === 'SELECT' && !hasOption(field, value)) {
                return;
            }

            field.value = value;
        });
    }

    /**
     * Pre-select an intent on the enquiry form inside `section` and open its branch.
     * The dropdown stays editable. Returns the opened branch element, if any.
     */
    function applyIntent(section, intent) {
        var form = section.querySelector('[data-partners-form]');
        var select = form ? form.querySelector('[data-partners-intent]') : null;

        if (!select) {
            return null;
        }

        // CTAs that carry no intent fall back to the section default, i.e. the first
        // option that opens the demand form.
        var wanted = intent || section.getAttribute('data-partners-default-intent') || '';

        if (wanted && hasOption(select, wanted)) {
            select.value = wanted;
        }

        var branch = applyBranch(form);

        return branch ? branchEl(form, branch) : null;
    }

    /** Height to scroll past, which on desktop is zero because the header is in flow. */
    function headerOffset() {
        var header = document.getElementById('main-header');

        if (!header || window.getComputedStyle(header).position !== 'fixed') {
            return 0;
        }

        return header.getBoundingClientRect().height;
    }

    function scrollToSection(section, smooth) {
        var top = section.getBoundingClientRect().top + window.pageYOffset - headerOffset() - SCROLL_GAP;

        window.scrollTo({
            top: Math.max(top, 0),
            behavior: smooth ? 'smooth' : 'auto'
        });
    }

    function focusFirstEmpty(scope) {
        if (!scope) {
            return;
        }

        var target = null;

        scope.querySelectorAll('input, select, textarea').forEach(function (field) {
            if (target || field.disabled || field.type === 'hidden' || field.tabIndex === -1) {
                return;
            }

            if (!field.value) {
                target = field;
            }
        });

        if (target) {
            // The smooth scroll is already running; focusing must not fight it.
            target.focus({ preventScroll: true });
        }
    }

    function prefersReducedMotion() {
        return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    /** The section a target points at, but only when it holds an intent dropdown. */
    function intentSection(target) {
        var section = target ? document.getElementById(target.id) : null;

        if (!section || !section.querySelector('[data-partners-intent]')) {
            return null;
        }

        return section;
    }

    function goToForm(section, target, smooth) {
        var branch = applyIntent(section, target.intent);

        prefillBranch(branch, target.values);
        scrollToSection(section, smooth);

        // Runs after the prefill so the visitor lands on the first field still to answer.
        focusFirstEmpty(branch);
    }

    /**
     * Every in-page CTA that points at the enquiry form goes through here, so the hero
     * button, the apply rails, the API panel and the supplier banner all behave the same.
     */
    function initCtas() {
        document.addEventListener('click', function (event) {
            if (event.defaultPrevented || event.button !== 0 ||
                event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            var link = event.target.closest ? event.target.closest('a[href*="#"]') : null;

            if (!link) {
                return;
            }

            var url;

            try {
                url = new URL(link.getAttribute('href'), window.location.href);
            } catch (e) {
                return;
            }

            // Links from another page are left alone; the hash is applied on load there.
            if (url.host !== window.location.host || url.pathname !== window.location.pathname) {
                return;
            }

            var target = parseTarget(url.hash);
            var section = intentSection(target);

            if (!section) {
                return;
            }

            event.preventDefault();

            goToForm(section, target, !prefersReducedMotion());

            if (window.history && window.history.replaceState) {
                window.history.replaceState(null, '', '#' + target.id);
            }
        });
    }

    /**
     * Deep links such as /partners/#partners-contact?intent=supplier. The browser cannot
     * jump to that hash on its own, and pre-selecting an intent changes the page height,
     * so the position is set here instead.
     */
    function applyHashOnLoad() {
        var target = parseTarget(window.location.hash);
        var section = intentSection(target);

        if (!section) {
            return;
        }

        goToForm(section, target, false);
    }

    document.addEventListener('DOMContentLoaded', function () {
        document.querySelectorAll('[data-partners-form]').forEach(initForm);
        initCtas();
        applyHashOnLoad();
    });
})();
